import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Loader2,
  Search,
  Crosshair,
  CheckCircle2,
  RotateCw,
  Clock,
  ChevronRight,
  X,
  FilePlus2,
  Brain,
  Send,
  ExternalLink,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "@/hooks/use-toast";
import { useOwnClientId } from "@/hooks/useOwnClientId";

// ── palette ────────────────────────────────────────────────────────────────
const GREEN = "#00FF88";
const PURPLE = "#8B5CF6";
const AMBER = "#F59E0B";
const BLUE = "#3B82F6";
const BG = "#0A0A14";
const CARD = "rgba(255,255,255,0.025)";
const CARD_BORDER = "rgba(255,255,255,0.08)";

interface QuestionRow {
  id: string;
  keyword: string;
  question_text: string;
  intent_category: string | null;
}

interface CoverageRow {
  question_id: string;
  covered: boolean;
  gap_priority: number;
  coverage_type: string | null;
  covered_by: string | null;
}

type StatusKey = "captured" | "in_progress" | "queued";

const statusFor = (cov: CoverageRow | undefined): { key: StatusKey; label: string; color: string } => {
  if (cov?.covered) return { key: "captured", label: "CAPTURED", color: GREEN };
  if ((cov?.gap_priority ?? 0) > 50) return { key: "in_progress", label: "IN PROGRESS", color: PURPLE };
  return { key: "queued", label: "QUEUED", color: AMBER };
};

const truncate = (s: string, words = 10) => {
  const parts = s.split(/\s+/);
  return parts.length <= words ? s : parts.slice(0, words).join(" ") + "…";
};

// ── radar visual (compact, top-right of capture block) ─────────────────────
const MiniRadar = () => (
  <div className="relative aspect-square w-[140px] sm:w-[180px]">
    {[30, 55, 80, 100].map((pct, i) => (
      <div
        key={i}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{ width: `${pct}%`, height: `${pct}%`, border: `1px solid rgba(139,92,246,${0.18 + i * 0.05})` }}
      />
    ))}
    <div
      className="absolute left-1/2 top-1/2 origin-left"
      style={{
        width: "50%",
        height: "2px",
        background: `linear-gradient(90deg, ${PURPLE} 0%, rgba(139,92,246,0) 100%)`,
        boxShadow: `0 0 14px rgba(139,92,246,0.7)`,
        transformOrigin: "left center",
        animation: "ai-cap-sweep 6s linear infinite",
      }}
    />
    <div className="absolute right-[8%] top-[18%] h-3 w-3 rounded-full" style={{ background: PURPLE, boxShadow: `0 0 18px ${PURPLE}` }} />
    <style>{`@keyframes ai-cap-sweep { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ── Question detail modal ──────────────────────────────────────────────────
const QuestionDetailModal = ({
  open,
  onClose,
  question,
  cov,
  coveredEntity,
}: {
  open: boolean;
  onClose: () => void;
  question: QuestionRow | null;
  cov: CoverageRow | undefined;
  coveredEntity: { kind: string; title: string; slug?: string | null } | null;
}) => {
  if (!open || !question) return null;
  const s = statusFor(cov);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="relative w-full max-w-xl overflow-hidden rounded-[18px]"
        style={{ background: BG, border: `1px solid ${CARD_BORDER}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div className="text-[14px] font-[700] uppercase tracking-wider text-white/70">Question Detail</div>
          <button onClick={onClose} className="rounded-full p-1 text-white/60 hover:bg-white/10 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-4 p-6">
          <div className="text-[20px] font-[800] leading-snug text-white">{question.question_text}</div>
          <div className="flex flex-wrap items-center gap-2 text-[12px]">
            <span className="text-white/50">From keyword:</span>
            <span className="rounded-full px-2 py-0.5 font-[600]" style={{ background: "rgba(139,92,246,0.15)", color: PURPLE }}>
              {question.keyword}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="rounded-full px-2.5 py-1 text-[11px] font-[800]"
              style={{ background: `${s.color}1f`, color: s.color, border: `1px solid ${s.color}55` }}
            >
              {s.label}
            </span>
            {!cov?.covered && (
              <span className="text-[12px] text-white/55">Gap priority: {cov?.gap_priority ?? 0}</span>
            )}
          </div>
          {cov?.covered && coveredEntity ? (
            <div className="rounded-[12px] p-3" style={{ background: CARD, border: `1px solid ${CARD_BORDER}` }}>
              <div className="text-[11px] uppercase tracking-wider text-white/50">Covered by</div>
              <div className="mt-1 flex items-center gap-2 text-[13px] text-white">
                <ExternalLink size={14} style={{ color: GREEN }} />
                <span className="font-[600]">{coveredEntity.title}</span>
                <span className="text-white/40">· {coveredEntity.kind}</span>
              </div>
            </div>
          ) : (
            <div className="rounded-[12px] p-3 text-[13px] text-white/70" style={{ background: CARD, border: `1px solid ${CARD_BORDER}` }}>
              Being worked on — a dedicated answer is being prepared.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Full questions list modal ──────────────────────────────────────────────
const AllQuestionsModal = ({
  open,
  onClose,
  questions,
  coverageMap,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  questions: QuestionRow[];
  coverageMap: Map<string, CoverageRow>;
  onPick: (q: QuestionRow) => void;
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="relative max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-[18px]"
        style={{ background: BG, border: `1px solid ${CARD_BORDER}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div className="text-[16px] font-[800] text-white">All Captured Questions</div>
          <button onClick={onClose} className="rounded-full p-1 text-white/60 hover:bg-white/10 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[65vh] overflow-y-auto p-4">
          <div className="flex flex-col gap-2">
            {questions.map((q) => {
              const s = statusFor(coverageMap.get(q.id));
              return (
                <button
                  key={q.id}
                  onClick={() => onPick(q)}
                  className="flex items-center justify-between gap-3 rounded-[12px] p-3 text-left transition hover:border-white/20"
                  style={{ background: CARD, border: `1px solid ${CARD_BORDER}` }}
                >
                  <div className="flex-1">
                    <div className="text-[13px] font-[600] text-white">{q.question_text}</div>
                    <div className="mt-0.5 text-[11px] text-white/50">{q.keyword}</div>
                  </div>
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-[800]"
                    style={{ background: `${s.color}1f`, color: s.color, border: `1px solid ${s.color}55` }}
                  >
                    {s.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── card ───────────────────────────────────────────────────────────────────
const AiRadarCard = () => {
  const isMobile = useIsMobile();
  const { ownClientId } = useOwnClientId("AiCaptureBoard");
  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [coverage, setCoverage] = useState<CoverageRow[]>([]);
  const [answerPagesCount, setAnswerPagesCount] = useState(0);
  const [postsCount, setPostsCount] = useState(0);
  const [bingIndexedCount, setBingIndexedCount] = useState(0);
  const [keywordsCount, setKeywordsCount] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [detailQ, setDetailQ] = useState<QuestionRow | null>(null);
  const [coveredEntity, setCoveredEntity] = useState<{ kind: string; title: string; slug?: string | null } | null>(null);
  const [allOpen, setAllOpen] = useState(false);

  const load = async () => {
    if (!ownClientId) return;
    const [q, c, a, p, bi, kw] = await Promise.all([
      supabase.from("ai_questions").select("id, keyword, question_text, intent_category").eq("client_id", ownClientId),
      supabase.from("ai_question_coverage").select("question_id, covered, gap_priority, coverage_type, covered_by").eq("client_id", ownClientId),
      supabase.from("ai_answer_pages").select("id", { count: "exact", head: true }).eq("client_id", ownClientId),
      supabase.from("blog_posts").select("id", { count: "exact", head: true }).eq("client_id", ownClientId),
      supabase.from("blog_posts").select("id", { count: "exact", head: true }).eq("client_id", ownClientId).eq("indexed_google", true),
      supabase.from("keywords").select("id", { count: "exact", head: true }).eq("client_id", ownClientId),
    ]);
    setQuestions((q.data as QuestionRow[]) || []);
    setCoverage((c.data as CoverageRow[]) || []);
    setAnswerPagesCount(a.count || 0);
    setPostsCount(p.count || 0);
    setBingIndexedCount(bi.count || 0);
    setKeywordsCount(kw.count || 0);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownClientId]);

  const coverageMap = useMemo(() => {
    const m = new Map<string, CoverageRow>();
    for (const c of coverage) m.set(c.question_id, c);
    return m;
  }, [coverage]);

  const totals = useMemo(() => {
    let captured = 0;
    let inProgress = 0;
    let queued = 0;
    for (const q of questions) {
      const cov = coverageMap.get(q.id);
      if (cov?.covered) captured++;
      else if ((cov?.gap_priority ?? 0) > 50) inProgress++;
      else queued++;
    }
    const total = questions.length;
    const pct = total > 0 ? Math.round((captured / total) * 100) : 0;
    return { captured, inProgress, queued, total, pct };
  }, [questions, coverageMap]);

  // Today's capture: highest gap_priority uncovered question
  const todays = useMemo(() => {
    const candidates = questions
      .map((q) => ({ q, cov: coverageMap.get(q.id) }))
      .filter((x) => !x.cov?.covered)
      .sort((a, b) => (b.cov?.gap_priority ?? 0) - (a.cov?.gap_priority ?? 0));
    return candidates[0] || null;
  }, [questions, coverageMap]);

  const todaysProgressPct = totals.pct;

  // Question list (4 rows): sort uncovered first by gap_priority desc, then covered last
  const topRows = useMemo(() => {
    return [...questions]
      .map((q) => ({ q, cov: coverageMap.get(q.id) }))
      .sort((a, b) => {
        const ac = a.cov?.covered ? 1 : 0;
        const bc = b.cov?.covered ? 1 : 0;
        if (ac !== bc) return ac - bc;
        return (b.cov?.gap_priority ?? 0) - (a.cov?.gap_priority ?? 0);
      })
      .slice(0, 4);
  }, [questions, coverageMap]);

  const openDetail = async (q: QuestionRow) => {
    setDetailQ(q);
    setCoveredEntity(null);
    const cov = coverageMap.get(q.id);
    if (cov?.covered && cov.covered_by) {
      // try answer pages first, then blog posts
      const ap = await supabase
        .from("ai_answer_pages")
        .select("page_title, page_slug")
        .eq("id", cov.covered_by)
        .maybeSingle();
      if (ap.data) {
        setCoveredEntity({ kind: "Answer page", title: (ap.data as any).page_title, slug: (ap.data as any).page_slug });
        return;
      }
      const bp = await supabase
        .from("blog_posts")
        .select("title, slug")
        .eq("id", cov.covered_by)
        .maybeSingle();
      if (bp.data) {
        setCoveredEntity({ kind: "Blog post", title: (bp.data as any).title, slug: (bp.data as any).slug });
      }
    }
  };

  const findNew = async () => {
    if (!ownClientId) return;
    const { blockIfDemo } = await import("@/lib/demoMode");
    if (blockIfDemo("Finding new questions")) return;
    setScanning(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-ai-questions", { body: { client_id: ownClientId } });
      if (error || (data as any)?.error) {
        toast({ title: "Scan failed", description: (data as any)?.error || error?.message || "Unknown", variant: "destructive" });
      } else {
        toast({ title: "New questions discovered and added" });
        await load();
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setScanning(false);
    }
  };

  const engineOn = keywordsCount > 0;
  const todaysStatus = (todays?.cov?.gap_priority ?? 0) > 50 ? "IN PROGRESS" : "QUEUED";
  const todaysStatusColor = todaysStatus === "IN PROGRESS" ? PURPLE : AMBER;

  return (
    <div
      className="mb-[18px] overflow-hidden rounded-[20px]"
      style={{
        background: BG,
        border: `1px solid ${CARD_BORDER}`,
        boxShadow: "0 30px 80px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      {/* header */}
      <div className={`flex ${isMobile ? "flex-col gap-3" : "items-start justify-between"} px-6 pt-6`}>
        <div className="flex items-start gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl"
            style={{ background: "rgba(0,255,136,0.10)", border: `1px solid ${GREEN}55` }}
          >
            <Crosshair className="h-5 w-5" style={{ color: GREEN }} />
          </div>
          <div>
            <h2 className="text-[26px] font-[900] tracking-[-0.02em] leading-none text-white">AI Capture Board</h2>
            <p className="mt-1.5 text-[13px] text-white/55">Every question people ask AI about your business — captured.</p>
          </div>
        </div>
        <div
          className="flex items-center gap-2 rounded-[12px] px-3 py-2"
          style={{ background: CARD, border: `1px solid ${GREEN}55` }}
        >
          <Crosshair size={14} style={{ color: GREEN }} />
          <span className="text-[12px] font-[700] text-white/80">Capture Engine:</span>
          <span className="text-[12px] font-[800]" style={{ color: GREEN }}>{engineOn ? "ON" : "IDLE"}</span>
          <span
            className="ml-1 inline-block h-2 w-2 rounded-full"
            style={{ background: GREEN, boxShadow: `0 0 10px ${GREEN}`, animation: "ai-cap-pulse 1.5s ease-in-out infinite" }}
          />
        </div>
      </div>

      <div className="px-3 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
        {/* TODAY'S CAPTURE */}
        <div
          data-tour-id="ai-capture-main"
          className="relative overflow-hidden rounded-[16px] p-3 sm:p-5"
          style={{
            background: "linear-gradient(180deg, rgba(139,92,246,0.06), rgba(139,92,246,0.02))",
            border: `1.5px solid ${PURPLE}80`,
            boxShadow: `0 0 28px rgba(139,92,246,0.25), inset 0 0 30px rgba(139,92,246,0.06)`,
          }}
        >
          {/* purple corner brackets */}
          <span className="pointer-events-none absolute left-2 top-2 h-4 w-4 border-l-2 border-t-2" style={{ borderColor: PURPLE }} />
          <span className="pointer-events-none absolute right-2 top-2 h-4 w-4 border-r-2 border-t-2" style={{ borderColor: PURPLE }} />
          <span className="pointer-events-none absolute bottom-2 left-2 h-4 w-4 border-b-2 border-l-2" style={{ borderColor: PURPLE }} />
          <span className="pointer-events-none absolute bottom-2 right-2 h-4 w-4 border-b-2 border-r-2" style={{ borderColor: PURPLE }} />

          <div className={`flex ${isMobile ? "flex-col items-center" : "items-start justify-between"} gap-4`}>
            <div className="flex-1 w-full min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-[800] uppercase tracking-[0.18em]" style={{ color: PURPLE }}>Today's Capture</div>
                <span
                  className="shrink-0 rounded-full px-2.5 py-1 text-[10px] font-[800]"
                  style={{ background: `${todaysStatusColor}22`, color: todaysStatusColor, border: `1px solid ${todaysStatusColor}66` }}
                >
                  {todaysStatus === "IN PROGRESS" ? <RotateCw size={10} className="mr-1 inline animate-spin" /> : <Clock size={10} className="mr-1 inline" />}
                  {todaysStatus}
                </span>
              </div>
              <div className="mt-3 text-[18px] font-[900] leading-tight text-white sm:text-[30px]">
                {todays ? todays.q.question_text : (questions.length === 0 ? "Scanning for AI questions…" : "All caught up — no open captures.")}
              </div>
              {todays && (
                <div className="mt-2 text-[13px] text-white/60">
                  from: <span style={{ color: PURPLE }} className="font-[600]">{todays.q.keyword}</span>
                </div>
              )}
              {!todays && questions.length === 0 && (
                <div className="mt-2 text-[13px] text-white/60">Click <span className="font-[700] text-white/80">Find New Questions</span> to start.</div>
              )}
            </div>
            <div className={isMobile ? "mx-auto" : ""}>
              <MiniRadar />
            </div>
          </div>

          <button
            onClick={() => setAllOpen(true)}
            className="mt-5 block w-full text-left"
            aria-label="Open all questions"
          >
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[12px] font-[600] text-white/70">Capture Progress</span>
              <span className="text-[14px] font-[800] text-white">{todaysProgressPct}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.08)" }}>
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${todaysProgressPct}%`,
                  background: `linear-gradient(90deg, ${PURPLE}, #c4b5fd)`,
                  boxShadow: `0 0 14px ${PURPLE}80`,
                }}
              />
            </div>
          </button>
        </div>

        {/* THREE NUMBERS + total coverage */}
        <div
          data-tour-id="ai-coverage"
          className="mt-4 rounded-[16px] p-3 sm:p-5"
          style={{ background: CARD, border: `1px solid ${CARD_BORDER}` }}
        >
          <div className={`grid ${isMobile ? "grid-cols-1 gap-4" : "grid-cols-3 gap-6"}`}>
            <StatTile color={GREEN} icon={<CheckCircle2 size={22} style={{ color: GREEN }} />} bg="rgba(0,255,136,0.10)" border={`${GREEN}55`} value={totals.captured} label="CAPTURED" desc="We secured these questions" />
            <StatTile color={PURPLE} icon={<RotateCw size={22} style={{ color: PURPLE }} className="animate-spin-slow" />} bg="rgba(139,92,246,0.10)" border={`${PURPLE}55`} value={totals.inProgress} label="IN PROGRESS" desc="Being captured now" />
            <StatTile color={AMBER} icon={<Clock size={22} style={{ color: AMBER }} />} bg="rgba(245,158,11,0.10)" border={`${AMBER}55`} value={totals.queued} label="QUEUED" desc="Being set up" />
          </div>

          <button onClick={() => setAllOpen(true)} className="mt-5 block w-full text-left">
            <div className="flex items-center justify-between">
              <span className="text-[14px] font-[700] text-white">Total Coverage</span>
              <div className="text-right">
                <div className="text-[20px] font-[900]" style={{ color: GREEN }}>{totals.pct}%</div>
                <div className="text-[11px] text-white/50">{totals.captured} of {totals.total} questions</div>
              </div>
            </div>
            <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.08)" }}>
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${totals.pct}%`, background: GREEN, boxShadow: `0 0 14px ${GREEN}80` }}
              />
            </div>
          </button>
        </div>

        {/* QUESTION LIST */}
        <div
          className="mt-4 overflow-hidden rounded-[16px]"
          style={{ background: CARD, border: `1px solid ${CARD_BORDER}` }}
        >
          <div className="grid grid-cols-[1fr_auto] items-center gap-2 border-b border-white/8 px-3 py-3 text-[11px] font-[700] uppercase tracking-wider text-white/50 sm:px-5">
            <div>Question · <span className="text-white/40">From keyword</span></div>
            <div>Status</div>
          </div>
          {topRows.length === 0 ? (
            <div className="px-5 py-6 text-center text-[13px] text-white/55">
              {questions.length === 0
                ? "Scanning for AI questions… · Click Find New Questions to start"
                : "0 of 0 questions · Click Find New Questions to begin"}
            </div>
          ) : (
            topRows.map(({ q, cov }) => {
              const s = statusFor(cov);
              return (
                <button
                  key={q.id}
                  onClick={() => openDetail(q)}
                  className="grid w-full grid-cols-[auto_1fr_auto_auto] items-center gap-3 border-t border-white/5 px-3 py-3 text-left transition hover:bg-white/[0.03] sm:px-5"
                >
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-full"
                    style={{ background: `${s.color}18`, border: `1px solid ${s.color}55` }}
                  >
                    <Crosshair size={14} style={{ color: s.color }} />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-[600] text-white">{truncate(q.question_text, 10)}</div>
                    <div className="truncate text-[11px] text-white/45">{q.keyword}</div>
                  </div>
                  <span
                    className="hidden shrink-0 rounded-full px-2.5 py-1 text-[10px] font-[800] sm:inline-flex sm:items-center sm:gap-1"
                    style={{ background: `${s.color}1a`, color: s.color, border: `1px solid ${s.color}55` }}
                  >
                    {s.key === "captured" && <CheckCircle2 size={11} />}
                    {s.key === "in_progress" && <RotateCw size={11} className="animate-spin" />}
                    {s.key === "queued" && <Clock size={11} />}
                    {s.label}
                  </span>
                  <ChevronRight size={16} className="text-white/35" />
                </button>
              );
            })
          )}
        </div>

        {/* WHAT'S HAPPENING NOW */}
        <div className="mt-5">
          <div className="mb-3 text-[11px] font-[700] uppercase tracking-[0.18em] text-white/45">What's Happening Now</div>
          <div className={`grid gap-3 ${isMobile ? "grid-cols-1" : "grid-cols-3"}`}>
            <MissionCard
              icon={<FilePlus2 size={20} style={{ color: GREEN }} />}
              dot={GREEN}
              title="Capturing Questions"
              desc="Building new content for uncovered opportunities."
              count={`${answerPagesCount} answer page${answerPagesCount === 1 ? "" : "s"} created`}
              accent={GREEN}
            />
            <MissionCard
              icon={<Brain size={20} style={{ color: PURPLE }} />}
              dot={PURPLE}
              title="Teaching AI"
              desc="Optimizing entity signals across all content."
              count={`${postsCount + answerPagesCount} signals structured`}
              accent={PURPLE}
            />
            <MissionCard
              icon={<Send size={20} style={{ color: BLUE }} />}
              dot={BLUE}
              title="Indexing"
              desc="Submitting to Google & Bing instantly."
              count={`${bingIndexedCount} pages indexed`}
              accent={BLUE}
            />
          </div>
        </div>

        {/* FIND NEW QUESTIONS */}
        <button
          onClick={findNew}
          disabled={scanning || !ownClientId}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-[14px] px-4 py-3.5 text-[14px] font-[800] tracking-wide transition disabled:opacity-60"
          style={{
            background: "rgba(0,255,136,0.06)",
            border: `1.5px solid ${GREEN}`,
            color: GREEN,
            boxShadow: `0 0 22px rgba(0,255,136,0.18), inset 0 0 18px rgba(0,255,136,0.04)`,
          }}
        >
          {scanning ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
          {scanning ? "Scanning…" : "Find New Questions"}
        </button>
      </div>

      <style>{`
        @keyframes ai-cap-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.55; transform: scale(1.4); } }
        .animate-spin-slow { animation: spin 4s linear infinite; }
      `}</style>

      <QuestionDetailModal
        open={!!detailQ}
        onClose={() => setDetailQ(null)}
        question={detailQ}
        cov={detailQ ? coverageMap.get(detailQ.id) : undefined}
        coveredEntity={coveredEntity}
      />
      <AllQuestionsModal
        open={allOpen}
        onClose={() => setAllOpen(false)}
        questions={[...questions].sort((a, b) => {
          const ac = coverageMap.get(a.id)?.covered ? 1 : 0;
          const bc = coverageMap.get(b.id)?.covered ? 1 : 0;
          if (ac !== bc) return ac - bc;
          return (coverageMap.get(b.id)?.gap_priority ?? 0) - (coverageMap.get(a.id)?.gap_priority ?? 0);
        })}
        coverageMap={coverageMap}
        onPick={(q) => {
          setAllOpen(false);
          openDetail(q);
        }}
      />
    </div>
  );
};

const StatTile = ({
  icon,
  bg,
  border,
  value,
  label,
  desc,
  color,
}: {
  icon: React.ReactNode;
  bg: string;
  border: string;
  value: number;
  label: string;
  desc: string;
  color: string;
}) => (
  <div className="flex items-center gap-4">
    <div
      className="flex h-14 w-14 items-center justify-center rounded-full"
      style={{ background: bg, border: `1px solid ${border}` }}
    >
      {icon}
    </div>
    <div>
      <div className="text-[28px] font-[900] leading-none" style={{ color }}>{value}</div>
      <div className="mt-1 text-[11px] font-[800] tracking-wider" style={{ color }}>{label}</div>
      <div className="mt-0.5 text-[11px] text-white/50">{desc}</div>
    </div>
  </div>
);

const MissionCard = ({
  icon,
  dot,
  title,
  desc,
  count,
  accent,
}: {
  icon: React.ReactNode;
  dot: string;
  title: string;
  desc: string;
  count: string;
  accent: string;
}) => (
  <div
    className="rounded-[14px] p-4"
    style={{ background: CARD, border: `1px solid ${CARD_BORDER}` }}
  >
    <div className="flex items-start justify-between">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-[10px]"
        style={{ background: `${accent}14`, border: `1px solid ${accent}40` }}
      >
        {icon}
      </div>
      <span
        className="mt-1 inline-block h-2.5 w-2.5 rounded-full"
        style={{ background: dot, boxShadow: `0 0 10px ${dot}` }}
      />
    </div>
    <div className="mt-3 text-[14px] font-[800] text-white">{title}</div>
    <div className="mt-1 text-[12px] leading-snug text-white/55">{desc}</div>
    <div className="mt-2 text-[11px] font-[700]" style={{ color: accent }}>{count}</div>
  </div>
);

export default AiRadarCard;
